import { imputationDocumentId } from '../pages/review-expense/utils/contract-expense-json';
import { readApiErrorCode } from './api-error';

const MILEAGE_TYPE1_ERROR_KEYS = new Set([
    'error_mileage_type1_departure_office_required',
    'error_mileage_type1_destination_office_required',
    'error_mileage_same_office',
]);

const MILEAGE_TYPE2_ERROR_KEYS = new Set([
    'error_mileage_type2_billable_required',
    'error_mileage_type2_description_required',
    'error_mileage_type2_matter_required',
    'error_mileage_type2_route_required',
]);

/** Read MatterId from header or first line item (supports legacy MatterID casing). */
export function resolveMatterIdFromJson(json) {
    const header = String(json?.MatterId ?? json?.MatterID ?? '').trim();
    if (header) {
        return header;
    }
    return String(json?.ListItem?.[0]?.MatterId ?? json?.ListItem?.[0]?.MatterID ?? '').trim();
}

/** Snapshot route + matter when the mileage expense modal opens (same editing session). */
export function captureMileageSessionBaseline(json, checksum, resolvedTravelType) {
    const origin = String(json?.AddressOrigin ?? '').trim();
    const dest = String(json?.AddressDestination ?? '').trim();
    const departureOfficeId = imputationDocumentId({ _id: json?.departure_office_id });
    const destinationOfficeId = imputationDocumentId({ _id: json?.destination_office_id });
    const travelMode = resolvedTravelType?.travel_mode
        ?? (departureOfficeId || destinationOfficeId ? 'office_travel' : null)
        ?? ((json?.travel_type ?? 'inter_office') === 'inter_office' ? 'office_travel' : 'other_travel');

    const baseline = {
        checksum,
        MatterId: resolveMatterIdFromJson(json),
        officeTravel: null,
        otherTravel: null,
    };

    if (travelMode === 'office_travel' || departureOfficeId || destinationOfficeId) {
        baseline.officeTravel = {
            departure_office_id: departureOfficeId,
            destination_office_id: destinationOfficeId,
            AddressOrigin: origin,
            AddressDestination: dest,
        };
    }
    if (travelMode === 'other_travel') {
        baseline.otherTravel = {
            AddressOrigin: origin,
            AddressDestination: dest,
        };
    }
    return baseline;
}

/** Restore baseline route/matter when switching back to a compatible travel type in the same session. */
export function restoreMileageSessionBaselineFields(json, nextTravelType, sessionBaseline) {
    if (!sessionBaseline || !nextTravelType) {
        return json;
    }
    const next = { ...json };

    if (nextTravelType.travel_mode === 'office_travel' && sessionBaseline.officeTravel) {
        const officeRoute = sessionBaseline.officeTravel;
        if (officeRoute.departure_office_id) {
            next.departure_office_id = officeRoute.departure_office_id;
        }
        if (officeRoute.destination_office_id) {
            next.destination_office_id = officeRoute.destination_office_id;
        }
        if (officeRoute.AddressOrigin) {
            next.AddressOrigin = officeRoute.AddressOrigin;
        }
        if (officeRoute.AddressDestination) {
            next.AddressDestination = officeRoute.AddressDestination;
        }
    }

    if (nextTravelType.travel_mode === 'other_travel' && sessionBaseline.otherTravel) {
        delete next.departure_office_id;
        delete next.destination_office_id;
        if (sessionBaseline.otherTravel.AddressOrigin) {
            next.AddressOrigin = sessionBaseline.otherTravel.AddressOrigin;
        }
        if (sessionBaseline.otherTravel.AddressDestination) {
            next.AddressDestination = sessionBaseline.otherTravel.AddressDestination;
        }
    }

    // XPEN-3424: a trip typed as an amount must not keep offices left over from a
    // previous mode. Unlike the other_travel branch above, this is not gated on a
    // captured sub-object — captureMileageSessionBaseline never populates one for
    // open_amount, since there is nothing route-shaped to snapshot for this mode.
    // The outer early return above is left as-is (not relaxed): the sole caller,
    // applyMileageTravelTypeSwitch, already clears these two fields itself before
    // calling this function for any non-route-preserving switch, so this is this
    // function's own defense-in-depth, not the only place the clearing happens.
    if (nextTravelType.travel_mode === 'open_amount') {
        delete next.departure_office_id;
        delete next.destination_office_id;
    }

    if (travelTypeRequiresMatter(nextTravelType) && sessionBaseline.MatterId) {
        next.MatterId = sessionBaseline.MatterId;
        next.ListItem = (next.ListItem || []).map((item) => ({
            ...item,
            MatterId: sessionBaseline.MatterId,
            IsBillable: 1,
        }));
    }
    return next;
}

/**
 * XPEN-2866: a Client-scoped travel type always needs a Matter — billed to the
 * client or not (write-off), it is still a disbursement line attached to a matter.
 * Billable Scope alone is not enough: a Client + Non-billable trip needs one too.
 */
export function travelTypeRequiresMatter(travelType) {
    return travelType?.client_scope === 'client';
}

/** Resolve a configured travel type row from expense rates. */
export function findMileageTravelType(rates, travelTypeId) {
    if (!travelTypeId) {
        return null;
    }
    return (rates?.mileage_travel_types ?? []).find((t) => t.id === travelTypeId) ?? null;
}

/** Drop validation keys from the other travel mode (e.g. after switching Non-Billable ↔ Billable). */
export function filterMileageErrorKeysForTravelType(keys, travelType) {
    const mode = travelType ?? 'inter_office';
    if (mode === 'billable') {
        return keys.filter((k) => !MILEAGE_TYPE1_ERROR_KEYS.has(k));
    }
    if (mode === 'inter_office') {
        return keys.filter((k) => !MILEAGE_TYPE2_ERROR_KEYS.has(k));
    }
    return keys;
}

/** Drop validation keys that do not apply to the resolved travel_type_id model. */
export function filterMileageErrorKeysForResolvedTravelType(keys, resolvedTravelType) {
    if (!resolvedTravelType) {
        return keys;
    }
    const { travel_mode } = resolvedTravelType;
    if (travel_mode === 'other_travel') {
        return keys.filter(
            (k) => !MILEAGE_TYPE1_ERROR_KEYS.has(k) && k !== 'error_mileage_open_amount_required'
        );
    }
    if (travel_mode === 'office_travel') {
        // Type 2 keys (including matter) never apply to office_travel — this second
        // pass is a no-op today, but keep it on the shared rule instead of a raw
        // billable_scope check so it doesn't silently drift again (XPEN-2866).
        let filtered = keys.filter(
            (k) => !MILEAGE_TYPE2_ERROR_KEYS.has(k) && k !== 'error_mileage_open_amount_required'
        );
        if (!travelTypeRequiresMatter(resolvedTravelType)) {
            filtered = filtered.filter((k) => k !== 'error_mileage_type2_matter_required');
        }
        return filtered;
    }
    if (travel_mode === 'open_amount') {
        // Neither an office pair nor a route applies to a typed amount — but the
        // matter key must still pass through for a Client-scoped type, the same
        // way other_travel already treats it (XPEN-2866).
        return keys.filter(
            (k) => !MILEAGE_TYPE1_ERROR_KEYS.has(k) && k !== 'error_mileage_type2_route_required'
        );
    }
    return keys;
}

/**
 * Client-side mileage field rules (XPEN-2508/2509).
 * Returns i18n keys (not translated strings) for Save-triggered feedback (XPEN-2550).
 * Translate at render time via data.t.read so messages stay visible after locale hot-reload.
 */
export function getMileageFieldErrorKeys(json, data) {
    if (json?.type !== 'mileage') {
        return [];
    }

    const travelType = json?.travel_type ?? 'inter_office';
    const keys = [];

    if (travelType === 'inter_office') {
        const depId = imputationDocumentId({ _id: json.departure_office_id });
        const destId = imputationDocumentId({ _id: json.destination_office_id });
        if (!depId) {
            keys.push('error_mileage_type1_departure_office_required');
        }
        if (!destId) {
            keys.push('error_mileage_type1_destination_office_required');
        }
        if (depId && destId && depId === destId) {
            keys.push('error_mileage_same_office');
        }
        return keys;
    }

    if (travelType === 'billable') {
        const desc = String(json?.ListItem?.[0]?.Description ?? '').trim();
        const matter = String(json?.MatterId ?? '').trim();
        const billable = [1, true].includes(json?.IsBillable);
        const origin = String(json?.AddressOrigin ?? '').trim();
        const dest = String(json?.AddressDestination ?? '').trim();
        if (!billable) {
            keys.push('error_mileage_type2_billable_required');
        }
        if (!desc) {
            keys.push('error_mileage_type2_description_required');
        }
        if (!matter) {
            keys.push('error_mileage_type2_matter_required');
        }
        if (!origin || !dest) {
            keys.push('error_mileage_type2_route_required');
        }
    }

    return keys;
}

/**
 * Validation for the new travel_type_id model (XPEN-2613).
 * Uses the resolved travel type's travel_mode and billable_scope
 * Uses travel_mode and billable_scope from the resolved travel type.
 * Returns i18n error keys (translate at render time).
 *
 * Three travel modes as of XPEN-3246: `office_travel` (pick departure/destination
 * offices), `other_travel` (type an address route), and `open_amount` (type the
 * amount spent directly — no office, no route). The Matter requirement below is
 * independent of mode: a Client-scoped travel type needs one regardless of how
 * the trip is priced (XPEN-2866).
 *
 * Single consumer: `src/components/expense.js` (the expense editor). The
 * create-trip form validates its own way and only imports `travelTypeRequiresMatter`
 * from this module (XPEN-3246-007).
 */
export function getMileageFieldErrorKeysForTravelType(json, resolvedTravelType) {
    if (!resolvedTravelType) return [];

    const keys = [];
    const { travel_mode } = resolvedTravelType;

    if (travel_mode === 'office_travel') {
        const depId = imputationDocumentId({ _id: json?.departure_office_id });
        const destId = imputationDocumentId({ _id: json?.destination_office_id });
        if (!depId) keys.push('error_mileage_type1_departure_office_required');
        if (!destId) keys.push('error_mileage_type1_destination_office_required');
        if (depId && destId && depId === destId) keys.push('error_mileage_same_office');
    }

    if (travel_mode === 'other_travel') {
        const origin = String(json?.AddressOrigin ?? '').trim();
        const dest = String(json?.AddressDestination ?? '').trim();
        if (!origin || !dest) keys.push('error_mileage_type2_route_required');
    }

    if (travel_mode === 'open_amount') {
        const amount = Number(json?.open_amount);
        if (!Number.isFinite(amount) || amount <= 0) {
            keys.push('error_mileage_open_amount_required');
        }
    }

    if (travelTypeRequiresMatter(resolvedTravelType)) {
        if (!resolveMatterIdFromJson(json)) {
            keys.push('error_mileage_type2_matter_required');
        }
    }

    return keys;
}

export function shouldDeferDualTravelPersist(data, expenseType) {
    return expenseType === 'mileage';
}

/** XPEN-2872 — only mileage keeps field edits local until Save; receipts auto-save. */
export function shouldDeferExpenseEditorAutoSave(expenseType) {
    // XPEN-2872: only mileage defers to a manual Save/Cancel footer. Receipts (and
    // legacy null-typed expenses) auto-save on every field change again — clients
    // asked to revert the receipt Save/Cancel/Restore UX from XPEN-2821/2822.
    return expenseType === 'mileage';
}

/**
 * XPEN-3481 — which sentence to show when saving a trip fails.
 *
 * The server answers `mileage_travel_type_forbidden` (403) when the chosen travel
 * type is reserved for a department or rank the report owner is not in. The trip
 * creation path used to discard the error object entirely, so a refusal showed the
 * traveller nothing at all — the spinner simply stopped.
 *
 * Owns the server-code-to-key mapping so the code literal lives in one place.
 * Callers differ on what a non-mileage failure should say, so they pass their own
 * fallback rather than each re-testing the code themselves.
 *
 * @param {object} error - The rejected api-wrapper error (an axios error).
 * @param {string} [fallbackKey] - Key to use for any other failure.
 * @returns {string} An i18n key. Read it through `data.t.read(...)`.
 */
export function mileageSaveErrorKey(error, fallbackKey = 'error_mileage_save_failed') {
    if (readApiErrorCode(error) === 'mileage_travel_type_forbidden') {
        return 'error_mileage_travel_type_forbidden';
    }
    return fallbackKey;
}
